import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { MENU } from "../data/menu";

export const FullMenu = () => {
  const [activeCat, setActiveCat] = useState(MENU[0].id);
  const sectionRefs = useRef({});
  const tabsRef = useRef(null);

  // observe section visibility to update active tab
  useEffect(() => {
    const observers = [];
    MENU.forEach((cat) => {
      const el = sectionRefs.current[cat.id];
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveCat(cat.id);
          });
        },
        { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // auto-scroll the active tab into view inside the horizontal scroller
  useEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const activeBtn = container.querySelector(`[data-cat="${activeCat}"]`);
    if (activeBtn) {
      const offset =
        activeBtn.offsetLeft -
        container.offsetWidth / 2 +
        activeBtn.offsetWidth / 2;
      container.scrollTo({ left: offset, behavior: "smooth" });
    }
  }, [activeCat]);

  const handleTabClick = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      id="menu"
      data-testid="full-menu-section"
      className="relative bg-cream py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-3 text-espresso/60 text-[12px] tracking-[0.3em] uppercase mb-5">
            <span className="h-px w-10 bg-espresso/40" />
            The Menu
            <span className="h-px w-10 bg-espresso/40" />
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-espresso text-balance">
            Read it like a love letter.
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-soft/65 text-[14px] leading-[1.8]">
            All prices are in Indian Rupees (₹). Add-ons available across coffee
            and burger sections. Some items take a little longer — slow is the
            point.
          </p>
        </div>
      </div>

      {/* Sticky horizontal tab bar */}
      <div className="sticky top-20 z-30 bg-cream/90 backdrop-blur-md border-y border-espresso/10">
        <div
          ref={tabsRef}
          className="max-w-[1400px] mx-auto px-6 md:px-10 flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide py-4"
          style={{ scrollbarWidth: "none" }}
          data-testid="menu-tabs"
        >
          {MENU.map((cat) => (
            <button
              key={cat.id}
              data-cat={cat.id}
              data-testid={`menu-tab-${cat.id}`}
              onClick={() => handleTabClick(cat.id)}
              className={`whitespace-nowrap px-5 py-2 text-[11px] tracking-[0.22em] uppercase border transition-all duration-400 ${
                activeCat === cat.id
                  ? "bg-espresso text-cream border-espresso"
                  : "bg-transparent text-espresso/70 border-espresso/20 hover:border-espresso/60"
              }`}
            >
              {cat.title}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 pb-10">
        {MENU.map((cat) => (
          <div
            key={cat.id}
            id={`menu-cat-${cat.id}`}
            ref={(el) => (sectionRefs.current[cat.id] = el)}
            data-testid={`menu-category-${cat.id}`}
            className="mb-20"
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="flex items-baseline justify-between gap-6 mb-10 border-b border-espresso/20 pb-4"
            >
              <h3 className="font-serif text-3xl md:text-4xl text-espresso italic">
                {cat.title}
              </h3>
              <span className="font-script text-xl text-gold">
                {String(cat.items.length).padStart(2, "0")}
              </span>
            </motion.div>

            <ul className="space-y-7">
              {cat.items.map((item, i) => (
                <motion.li
                  key={item.name}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                  className="group"
                  data-testid={`menu-item-${cat.id}-${i}`}
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h4 className="font-serif text-xl md:text-2xl text-espresso flex items-center gap-2">
                      {item.name}
                      {item.signature && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase text-gold border border-gold px-2 py-0.5 rounded-full"
                          data-testid={`signature-badge-${cat.id}-${i}`}
                        >
                          <Sparkles size={10} /> Signature
                        </span>
                      )}
                    </h4>
                    <span className="dot-leader" aria-hidden="true" />
                    <span className="font-serif text-xl text-espresso whitespace-nowrap">
                      ₹{item.price}
                    </span>
                  </div>
                  <p className="mt-1 text-[14px] text-soft/65 leading-[1.7] max-w-xl">
                    {item.desc}
                  </p>
                </motion.li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};
